import { useState, useMemo, useEffect } from "react"
import { agrogestao } from "@/api/agrogestaoClient"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import PartnerSelect from "@/components/shared/PartnerSelect"
import { Badge } from "@/components/ui/badge"
import { motion, AnimatePresence } from "framer-motion"
import { Plus, Edit2, Trash2, X, Tractor, Clock, Menu, Info, CheckCircle2, AlertCircle, DollarSign, ArrowRightLeft, Package, Calendar, Loader2, LineChart, Scale, Syringe, Milk, TrendingUp, TrendingDown, User, FileText, MoreHorizontal } from "lucide-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import ExportacaoRelatorios from "../components/financeiro/ExportacaoRelatorios"
import Paginacao from "../components/shared/Paginacao"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

import RebanhoChart from "@/components/dashboard/RebanhoChart"

const CATEGORIAS = [
  { value: 'bovino_corte', label: 'Bovino de Corte' },
  { value: 'bovino_leite', label: 'Bovino de Leite' },
  { value: 'suino', label: 'Suíno' },
  { value: 'aves', label: 'Aves' },
  { value: 'ovino', label: 'Ovinos' },
  { value: 'caprino', label: 'Caprinos' },
  { value: 'equino', label: 'Equinos' },
  { value: 'outro', label: 'Outros' }
]

function categoriaClasses(cat) {
  if (cat === 'bovino_leite') return { border: 'border-[#2D6A4F]', badge: 'bg-[#2D6A4F]/15 text-[#2D6A4F] border-[#2D6A4F]/30' }
  if (cat === 'bovino_corte') return { border: 'border-[#D4A373]', badge: 'bg-[#D4A373]/15 text-[#D4A373] border-[#D4A373]/30' }
  if (cat === 'suino') return { border: 'border-pink-400', badge: 'bg-pink-100 text-pink-700 border-pink-200' }
  if (cat === 'aves') return { border: 'border-amber-400', badge: 'bg-amber-100 text-amber-700 border-amber-200' }
  if (cat === 'ovino') return { border: 'border-lime-500', badge: 'bg-lime-100 text-lime-700 border-lime-200' }
  if (cat === 'caprino') return { border: 'border-violet-500', badge: 'bg-violet-100 text-violet-700 border-violet-200' }
  if (cat === 'equino') return { border: 'border-blue-500', badge: 'bg-blue-100 text-blue-700 border-blue-200' }
  return { border: 'border-stone-300', badge: 'bg-stone-100 text-stone-700 border-stone-200' }
}

export default function Animais() {
  const propriedadeId = localStorage.getItem("propriedade_selecionada")
  const [dialogOpen, setDialogOpen] = useState(false)
  const [moveDialog, setMoveDialog] = useState(null)
  const [moveTab, setMoveTab] = useState("dados")
  const [editingId, setEditingId] = useState(null)
  const [filterCategoria, setFilterCategoria] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(12)
  const [formData, setFormData] = useState({
    categoria: 'bovino_corte',
    quantidade: '',
    lote: '',
    peso_medio: '',
    data_entrada: new Date().toISOString().split('T')[0],
    custo_aquisicao: '',
    custo_tipo: 'total',
    observacoes: ''
  })
  const [moveData, setMoveData] = useState({ tipo: 'venda', quantidade: '', valor: '', condicao_pagamento: 'avista', parcelas: '', data_vencimento: new Date().toISOString().split('T')[0], frequencia: 'mensal', cliente: '', fornecedor: '', finalidade: '' })
  const [multiSelect, setMultiSelect] = useState(false)
  const [selectedIds, setSelectedIds] = useState([])
  const [filterMultiCat, setFilterMultiCat] = useState('')
  const [filterMultiLote, setFilterMultiLote] = useState('')
  const [histDialog, setHistDialog] = useState(null)
  const [inlineEditId, setInlineEditId] = useState(null)
  const [inlineEdit, setInlineEdit] = useState({ quantidade: '', peso_medio: '' })
  const [errorMsg, setErrorMsg] = useState("")
  const [histPage, setHistPage] = useState(1)
  const [histPageSize, setHistPageSize] = useState(10)
  const [pesPage, setPesPage] = useState(1)
  const [pesPageSize, setPesPageSize] = useState(10)
  const [manPage, setManPage] = useState(1)
  const [manPageSize, setManPageSize] = useState(10)
  const [leiteResumo, setLeiteResumo] = useState(null)
  const [novoManejo, setNovoManejo] = useState({ tipo: 'vacina', proxima_data: '' })
  const queryClient = useQueryClient()
  const [savingAnimal, setSavingAnimal] = useState(false)
  const [savingMove, setSavingMove] = useState(false)

  const formatCurrency = (value) => {
    if (typeof value === 'number') {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
    }
    return '-'
  }
  const formatNumber = (value, opts = {}) => {
    if (typeof value === 'number') {
      return new Intl.NumberFormat('pt-BR', opts).format(value)
    }
    return value || '-'
  }
  const formatDate = (iso) => {
    if (!iso) return '-'
    const d = new Date(iso)
    if (isNaN(d.getTime())) return iso
    return d.toLocaleDateString('pt-BR')
  }

  

  const { data: animais = [], isLoading } = useQuery({
    queryKey: ["animais", propriedadeId],
    queryFn: () => propriedadeId 
      ? agrogestao.entities.Animal.filter({ propriedade_id: propriedadeId }, "-created_date")
      : Promise.resolve([]),
    enabled: !!propriedadeId
  })

  const { data: historicos = [], isLoading: isHistLoading } = useQuery({
    queryKey: ["movimentos_animais", histDialog?.id],
    queryFn: () => (histDialog?.id && propriedadeId)
      ? agrogestao.entities.MovimentoAnimal.filter({ animal_id: histDialog.id, propriedade_id: propriedadeId }, "-data", 50)
      : Promise.resolve([]),
    enabled: !!histDialog?.id && !!propriedadeId
  })

  const { data: pesagens = [], isLoading: isPesLoading } = useQuery({
    queryKey: ["pesagens_animais", histDialog?.id],
    queryFn: () => (histDialog?.id && propriedadeId)
      ? agrogestao.entities.PesagemAnimal.filter({ animal_id: histDialog.id, propriedade_id: propriedadeId }, "-data", 50)
      : Promise.resolve([]),
    enabled: !!histDialog?.id && !!propriedadeId
  })

  const { data: manejos = [], isLoading: isManLoading } = useQuery({
    queryKey: ["manejos_animais", histDialog?.id],
    queryFn: () => (histDialog?.id && propriedadeId)
      ? agrogestao.entities.ManejoAnimal.filter({ animal_id: histDialog.id, propriedade_id: propriedadeId }, "-data", 50)
      : Promise.resolve([]),
    enabled: !!histDialog?.id && !!propriedadeId
  })

  const { data: movsProp = [] } = useQuery({
    queryKey: ["movs_prop", propriedadeId],
    queryFn: () => propriedadeId ? agrogestao.entities.MovimentoAnimal.filter({ propriedade_id: propriedadeId }, "-data", 500) : Promise.resolve([]),
    enabled: !!propriedadeId
  })

  useEffect(() => {
    const fetchLeite = async () => {
      if (!histDialog?.lote || !propriedadeId || histDialog?.categoria !== 'bovino_leite') { setLeiteResumo(null); return }
      const data = await agrogestao.entities.ProducaoLeite.filter({ propriedade_id: propriedadeId, lote: histDialog.lote }, "-data", 200)
      setLeiteResumo(data || [])
    }
    fetchLeite()
  }, [histDialog, propriedadeId])

  const animaisFiltrados = useMemo(() => {
    return animais.filter(a => {
      if (filterCategoria && a.categoria !== filterCategoria) return false
      if (searchTerm) {
        const s = searchTerm.toLowerCase()
        const l = (a.lote || '').toLowerCase()
        const o = (a.observacoes || '').toLowerCase()
        if (!l.includes(s) && !o.includes(s)) return false
      }
      return true
    })
  }, [animais, filterCategoria, searchTerm])

  const start = (currentPage - 1) * pageSize
  const animaisPagina = useMemo(() => animaisFiltrados.slice(start, start + pageSize), [animaisFiltrados, start, pageSize])

  const totalGrupos = animais.length
  const totalCabecas = useMemo(() => animais.reduce((acc, a) => acc + (a.quantidade || 0), 0), [animais])
  const totalLeite = useMemo(() => animais.filter(a => a.categoria === 'bovino_leite').reduce((acc, a) => acc + (a.quantidade || 0), 0), [animais])
  const totalCorte = useMemo(() => animais.filter(a => a.categoria === 'bovino_corte').reduce((acc, a) => acc + (a.quantidade || 0), 0), [animais])

  const porCategoria = useMemo(() => {
    const map = new Map()
    animais.forEach(a => map.set(a.categoria, (map.get(a.categoria) || 0) + (a.quantidade || 0)))
    return Array.from(map.entries()).map(([cat, qtd]) => ({ cat, qtd, label: CATEGORIAS.find(c => c.value === cat)?.label || cat }))
  }, [animais])

  const meses = useMemo(() => {
    const arr = []
    const d = new Date()
    d.setHours(0,0,0,0)
    for (let i = 11; i >= 0; i--) {
      const dt = new Date(d)
      dt.setMonth(d.getMonth() - i)
      arr.push({ key: `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}`, label: `${String(dt.getMonth()+1).padStart(2,'0')}/${String(dt.getFullYear()).slice(2)}` })
    }
    return arr
  }, [])

  const deltasPorMes = useMemo(() => {
    const map = new Map(meses.map(m => [m.key, 0]))
    movsProp.forEach(m => {
      const d = new Date(m.data)
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`
      if (!map.has(key)) return
      let delta = 0
      if (m.tipo === 'compra') delta = Number(m.quantidade || 0)
      else if (m.tipo === 'venda' || m.tipo === 'obito') delta = -Number(m.quantidade || 0)
      else if (m.tipo === 'ajuste') delta = (m.operacao === 'entrada' ? Number(m.quantidade || 0) : -Number(m.quantidade || 0))
      map.set(key, (map.get(key) || 0) + delta)
    })
    animais.forEach(a => {
      if (!a.data_entrada) return
      const d = new Date(a.data_entrada)
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`
      if (!map.has(key)) return
      const qtd = Number(a.quantidade || 0)
      map.set(key, (map.get(key) || 0) + qtd)
    })
    return map
  }, [movsProp, animais, meses])

  const chartData = useMemo(() => {
    const map = new Map(meses.map(m => [m.key, { mes: m.label, entradas: 0, saidas: 0 }]))
    movsProp.forEach(m => {
      const d = new Date(m.data)
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`
      if (!map.has(key)) return
      const entry = map.get(key)
      const qtd = Number(m.quantidade || 0)
      
      if (m.tipo === 'compra' || (m.tipo === 'ajuste' && m.operacao === 'entrada') || m.tipo === 'nascimento') {
        entry.entradas += qtd
      } else if (m.tipo === 'venda' || m.tipo === 'obito' || (m.tipo === 'ajuste' && m.operacao === 'saida')) {
        entry.saidas += qtd
      }
    })
    // Also consider animais data_entrada as "entradas" if they don't have a corresponding movement (initial load)
    // But usually creation creates a movement? The code for "Novo Registro" (createMutation) does NOT create a movement record automatically?
    // Let's check createMutation.
    // It creates Animal.
    // Does it create Movement? No.
    // So "initial stock" or "new animals" via form are NOT in movsProp unless manually moved?
    // Wait, createMutation just creates Animal.
    // So we should count `animais` with `data_entrada` as entradas too.
    animais.forEach(a => {
      if (!a.data_entrada) return
      const d = new Date(a.data_entrada)
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`
      if (!map.has(key)) return
      // Check if this animal creation is already logged as movement?
      // Since createMutation doesn't create movement, we assume it's an "entrada".
      const entry = map.get(key)
      entry.entradas += Number(a.quantidade || 0)
    })
    
    return Array.from(map.values())
  }, [meses, movsProp, animais])

  const evolucaoMensal = useMemo(() => {
    let current = totalCabecas
    const arr = []
    for (let i = meses.length - 1; i >= 0; i--) {
      const key = meses[i].key
      const delta = deltasPorMes.get(key) || 0
      const prev = current - delta
      arr.push({ key, label: meses[i].label, total: Math.max(0, prev) })
      current = prev
    }
    return arr.reverse()
  }, [totalCabecas, deltasPorMes, meses])

  const cutoff30 = useMemo(() => { const d = new Date(); d.setDate(d.getDate()-30); return d }, [])
  const movs30 = useMemo(() => movsProp.filter(m => new Date(m.data) >= cutoff30), [movsProp, cutoff30])
  const taxaObito30 = useMemo(() => {
    const o = movs30.filter(m => m.tipo === 'obito').reduce((acc, m) => acc + (Number(m.quantidade || 0)), 0)
    const delta30 = movs30.reduce((acc, m) => {
      const q = Number(m.quantidade || 0)
      if (m.tipo === 'compra') return acc + q
      if (m.tipo === 'venda' || m.tipo === 'obito') return acc - q
      if (m.tipo === 'ajuste') return acc + (m.operacao === 'entrada' ? q : -q)
      return acc
    }, 0)
    const inicioPeriodo = (totalCabecas || 0) - delta30
    const mediaPeriodo = Math.max(1, ((inicioPeriodo || 0) + (totalCabecas || 0)) / 2)
    return mediaPeriodo ? (o / mediaPeriodo) : 0
  }, [movs30, totalCabecas])
  const compras30 = useMemo(() => {
    const mov = movs30.filter(m => m.tipo === 'compra').reduce((acc, m) => acc + (Number(m.quantidade || 0)), 0)
    const entradas = animais
      .filter(a => a.data_entrada && new Date(a.data_entrada) >= cutoff30)
      .reduce((acc, a) => acc + (Number(a.quantidade || 0)), 0)
    return mov + entradas
  }, [movs30, animais, cutoff30])
  const vendas30 = useMemo(() => movs30.filter(m => m.tipo === 'venda').reduce((acc, m) => acc + (Number(m.quantidade || 0)), 0), [movs30])

  const { data: contasReceberProp = [] } = useQuery({
    queryKey: ["contas_receber_prop", propriedadeId],
    queryFn: () => propriedadeId ? agrogestao.entities.ContaReceber.filter({ propriedade_id: propriedadeId }, "-data_vencimento", 500) : Promise.resolve([]),
    enabled: !!propriedadeId
  })
  const { data: contasPagarProp = [] } = useQuery({
    queryKey: ["contas_pagar_prop", propriedadeId],
    queryFn: () => propriedadeId ? agrogestao.entities.ContaPagar.filter({ propriedade_id: propriedadeId }, "-data_vencimento", 500) : Promise.resolve([]),
    enabled: !!propriedadeId
  })
  const receitaVendas30 = useMemo(() => {
    return contasReceberProp
      .filter(c => c.categoria === 'Venda de Animais' && new Date(c.data_vencimento) >= cutoff30)
      .reduce((acc, c) => acc + (Number(c.valor || 0)), 0)
  }, [contasReceberProp, cutoff30])
  const custoCompras30 = useMemo(() => {
    const cp = contasPagarProp
      .filter(c => c.categoria === 'Compra de Animais' && new Date(c.data_vencimento) >= cutoff30)
      .reduce((acc, c) => acc + (Number(c.valor || 0)), 0)
    const aquis = animais
      .filter(a => a.data_entrada && new Date(a.data_entrada) >= cutoff30)
      .reduce((acc, a) => acc + (typeof a.custo_aquisicao === 'number' ? a.custo_aquisicao : 0), 0)
    return cp + aquis
  }, [contasPagarProp, animais, cutoff30])

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const u = await agrogestao.auth.me()
      const qtd = parseInt(data.quantidade || 0)
      const custoRaw = data.custo_aquisicao ? parseFloat(data.custo_aquisicao) : null
      const custoTotal = (typeof custoRaw === 'number')
        ? (data.custo_tipo === 'unitario' ? (qtd > 0 ? custoRaw * qtd : custoRaw) : custoRaw)
        : null
      const payload = {
        categoria: data.categoria,
        quantidade: qtd,
        lote: data.lote || null,
        peso_medio: data.peso_medio ? parseFloat(data.peso_medio) : null,
        data_entrada: data.data_entrada || null,
        custo_aquisicao: custoTotal,
        observacoes: data.observacoes || null,
        propriedade_id: propriedadeId,
        created_by: u?.email || null
      }
      return agrogestao.entities.Animal.create(payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["animais", propriedadeId] })
      resetForm()
    },
    onError: (err) => setErrorMsg(err?.message || "Erro ao salvar rebanho")
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => agrogestao.entities.Animal.update(id, {
      categoria: data.categoria,
      quantidade: parseInt(data.quantidade || 0),
      lote: data.lote || null,
      peso_medio: data.peso_medio ? parseFloat(data.peso_medio) : null,
      data_entrada: data.data_entrada || null,
      custo_aquisicao: (() => {
        const qtd = parseInt(data.quantidade || 0)
        const custoRaw = data.custo_aquisicao ? parseFloat(data.custo_aquisicao) : null
        if (typeof custoRaw !== 'number') return null
        return data.custo_tipo === 'unitario' ? (qtd > 0 ? custoRaw * qtd : custoRaw) : custoRaw
      })(),
      observacoes: data.observacoes || null
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["animais", propriedadeId] })
      resetForm()
    },
    onError: (err) => setErrorMsg(err?.message || "Erro ao atualizar rebanho")
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => agrogestao.entities.Animal.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["animais", propriedadeId] })
  })

  const resetForm = () => {
    setFormData({ categoria: 'bovino_corte', quantidade: '', lote: '', peso_medio: '', data_entrada: new Date().toISOString().split('T')[0], custo_aquisicao: '', custo_tipo: 'total', observacoes: '' })
    setEditingId(null)
    setDialogOpen(false)
    setErrorMsg("")
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const q = parseInt(formData.quantidade || 0)
    if (!q || q <= 0) { setErrorMsg("Informe a quantidade de cabeças (valor positivo)."); return }
    if (formData.peso_medio && parseFloat(formData.peso_medio) < 0) { setErrorMsg("Peso médio não pode ser negativo."); return }
    if (!formData.data_entrada) { setErrorMsg("Informe a data de entrada."); return }
    if (formData.custo_aquisicao) {
      const c = parseFloat(formData.custo_aquisicao)
      if (isNaN(c) || c < 0) { setErrorMsg("Custo de aquisição inválido."); return }
      if (formData.custo_tipo === 'unitario' && (!q || q <= 0)) { setErrorMsg("Para custo unitário, informe quantidade válida."); return }
    }
    setErrorMsg("")
    setSavingAnimal(true)
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: formData }, { onSettled: () => setSavingAnimal(false) })
    } else {
      createMutation.mutate(formData, { onSettled: () => setSavingAnimal(false) })
    }
  }

  const handleMovimentar = async () => {
    const item = moveDialog
    const tipo = moveData.tipo
    const qtd = parseInt(moveData.quantidade || 0)
    const valor = moveData.valor ? parseFloat(moveData.valor) : 0
    let financeiroTipo = null
    let financeiroId = null
    if (!item || !tipo || !qtd || qtd <= 0) { setErrorMsg("Preencha a quantidade válida"); return }
    if ((tipo === 'venda' || tipo === 'compra') && (!valor || valor <= 0)) { setErrorMsg("Informe o valor"); return }
    if (tipo === 'transferencia' && !moveData.lote_destino) { setErrorMsg("Informe o lote destino"); return }
    if ((tipo === 'venda') && !String(moveData.cliente || '').trim()) { setErrorMsg("Informe o cliente"); return }
    if ((tipo === 'compra') && !String(moveData.fornecedor || '').trim()) { setErrorMsg("Informe o fornecedor"); return }
    if ((tipo === 'venda' || tipo === 'compra') && !String(moveData.data_vencimento || '').trim()) { setErrorMsg("Informe o vencimento"); return }
    if ((tipo === 'venda' || tipo === 'compra') && moveData.condicao_pagamento === 'parcelado') {
      const p = parseInt(moveData.parcelas || 0)
      if (!p || p <= 0) { setErrorMsg("Informe o número de parcelas"); return }
    }
    const proceed = window.confirm("Confirmar execução da movimentação?")
    if (!proceed) return
    setErrorMsg("")
    setSavingMove(true)
    try {
      const targets = (multiSelect && selectedIds.length > 0) ? animais.filter(a => selectedIds.includes(a.id)) : [item]
      
      // Para óbito, criar um único lançamento financeiro agrupado
      let financeiroObitoId = null
      if (tipo === 'obito') {
        const uniqueCats = [...new Set(targets.map(t => t.categoria))]
        const catStr = uniqueCats.length === 1 ? (CATEGORIAS.find(c => c.value === uniqueCats[0])?.label || uniqueCats[0]) : 'Vários'
        const totalCabecas = qtd * targets.length
        
        const lc = await agrogestao.entities.Lancamento.create({
          tipo: 'despesa',
          categoria: 'Veterinário',
          descricao: `Óbito de animais (${catStr}) - ${totalCabecas} cabeça(s)`,
          valor: valor,
          data: new Date().toISOString().split('T')[0],
          propriedade_id: propriedadeId,
          status: 'pago'
        })
        financeiroObitoId = lc?.id
      }

      for (const it of targets) {
        const oldQty = typeof it.quantidade === 'number' ? it.quantidade : 0
        const oldCost = typeof it.custo_aquisicao === 'number' ? it.custo_aquisicao : 0
        const perHead = oldQty > 0 ? (oldCost / oldQty) : 0
        if (tipo === 'transferencia') {
          const novoTotal = it.quantidade - qtd
          if (novoTotal < 0) { setErrorMsg("Quantidade ficaria negativa"); return }
          const u = await agrogestao.auth.me()
          await agrogestao.entities.Animal.update(it.id, { quantidade: novoTotal })
          await agrogestao.entities.Animal.create({
            categoria: it.categoria,
            quantidade: qtd,
            lote: moveData.lote_destino || it.lote || null,
            peso_medio: it.peso_medio || null,
            data_entrada: new Date().toISOString().split('T')[0],
            custo_aquisicao: perHead > 0 ? (perHead * qtd) : null,
            observacoes: `Transferência do lote ${it.lote || ''}`.trim(),
            propriedade_id: propriedadeId,
            created_by: u?.email || null
          })
          if (perHead > 0) {
            const novoCusto = oldCost - (perHead * qtd)
            await agrogestao.entities.Animal.update(it.id, { custo_aquisicao: Math.max(0, novoCusto) })
          }
        } else if (tipo === 'ajuste') {
          const op = moveData.operacao || 'entrada'
          const novoTotal = op === 'entrada' ? (it.quantidade + qtd) : (it.quantidade - qtd)
          if (novoTotal < 0) { setErrorMsg("Quantidade ficaria negativa"); return }
          await agrogestao.entities.Animal.update(it.id, { quantidade: novoTotal })
        } else {
          const novoTotal = tipo === 'compra' ? (it.quantidade + qtd) : (it.quantidade - qtd)
          if (novoTotal < 0) { setErrorMsg("Quantidade ficaria negativa"); return }
          if (tipo === 'compra') {
            const novoCusto = oldCost + (valor || 0)
            await agrogestao.entities.Animal.update(it.id, { quantidade: novoTotal, custo_aquisicao: novoCusto })
          } else if (tipo === 'venda' || tipo === 'obito') {
            const reduzir = perHead * qtd
            const novoCusto = Math.max(0, oldCost - reduzir)
            await agrogestao.entities.Animal.update(it.id, { quantidade: novoTotal, custo_aquisicao: novoCusto })
          } else {
            await agrogestao.entities.Animal.update(it.id, { quantidade: novoTotal })
          }
          const hoje = new Date().toISOString().split('T')[0]
          if (tipo === 'venda') {
            const descBase = `Venda de animais (${it.categoria}) - lote ${it.lote || ''}`.trim()
            const venc = String(moveData.data_vencimento || hoje)
            const obs = `Qtd: ${qtd} • Unit: ${perHead > 0 ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(perHead) : '-'} • Condição: ${moveData.condicao_pagamento === 'parcelado' ? `${parseInt(moveData.parcelas || 0)}x ${moveData.frequencia}` : 'à vista'}`
            if (moveData.condicao_pagamento === 'parcelado' && parseInt(moveData.parcelas || 0) > 1) {
              const parcelas = parseInt(moveData.parcelas || 0)
              const valorTotal = valor
              const baseDate = new Date(venc)
              const valorParcela = Math.round((valorTotal / parcelas) * 100) / 100
              let firstId = null
              for (let i = 1; i <= parcelas; i++) {
                let due = new Date(baseDate)
                if (moveData.frequencia === 'semanal') due.setDate(baseDate.getDate() + 7 * (i - 1))
                else if (moveData.frequencia === 'quinzenal') due.setDate(baseDate.getDate() + 15 * (i - 1))
                else due.setMonth(baseDate.getMonth() + 1 * (i - 1))
                let v = valorParcela
                if (i === parcelas) {
                  v = Math.round((valorTotal - (valorParcela * (parcelas - 1))) * 100) / 100
                }
                const cr = await agrogestao.entities.ContaReceber.create({
                  propriedade_id: propriedadeId,
                  descricao: `${descBase} (${i}/${parcelas})`,
                  valor: v,
                  data_vencimento: due.toISOString().split('T')[0],
                  categoria: 'Venda de Animais',
                  cliente: moveData.cliente || '',
                  status: 'pendente',
                  observacoes: obs
                })
                if (!firstId) firstId = cr?.id || null
              }
              financeiroTipo = 'contas_receber'
              financeiroId = firstId
            } else {
              const existing = await agrogestao.entities.ContaReceber.filter({ descricao: descBase, data_vencimento: venc, categoria: 'Venda de Animais' }, "-data_vencimento", 5)
              const reuse = (existing || []).find(e => Number(e.valor || 0) === valor)
              if (reuse) {
                financeiroTipo = 'contas_receber'
                financeiroId = reuse.id
              } else {
                const cr = await agrogestao.entities.ContaReceber.create({
                  propriedade_id: propriedadeId,
                  descricao: descBase,
                  valor: valor,
                  data_vencimento: venc,
                  categoria: 'Venda de Animais',
                  cliente: moveData.cliente || '',
                  status: 'pendente',
                  observacoes: obs
                })
                financeiroTipo = 'contas_receber'
                financeiroId = cr?.id || null
              }
            }
          } else if (tipo === 'compra') {
            const descBase = `Compra de animais (${it.categoria}) - lote ${it.lote || ''}`.trim()
            const venc = String(moveData.data_vencimento || hoje)
            const obs = `Qtd: ${qtd} • Unit: ${perHead > 0 ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(perHead) : '-'} • Condição: ${moveData.condicao_pagamento === 'parcelado' ? `${parseInt(moveData.parcelas || 0)}x ${moveData.frequencia}` : 'à vista'}`
            if (moveData.condicao_pagamento === 'parcelado' && parseInt(moveData.parcelas || 0) > 1) {
              const parcelas = parseInt(moveData.parcelas || 0)
              const valorTotal = valor
              const baseDate = new Date(venc)
              const valorParcela = Math.round((valorTotal / parcelas) * 100) / 100
              let firstId = null
              for (let i = 1; i <= parcelas; i++) {
                let due = new Date(baseDate)
                if (moveData.frequencia === 'semanal') due.setDate(baseDate.getDate() + 7 * (i - 1))
                else if (moveData.frequencia === 'quinzenal') due.setDate(baseDate.getDate() + 15 * (i - 1))
                else due.setMonth(baseDate.getMonth() + 1 * (i - 1))
                let v = valorParcela
                if (i === parcelas) {
                  v = Math.round((valorTotal - (valorParcela * (parcelas - 1))) * 100) / 100
                }
                const cp = await agrogestao.entities.ContaPagar.create({
                  propriedade_id: propriedadeId,
                  descricao: `${descBase} (${i}/${parcelas})`,
                  valor: v,
                  data_vencimento: due.toISOString().split('T')[0],
                  categoria: 'Compra de Animais',
                  fornecedor: moveData.fornecedor || '',
                  status: 'pendente',
                  observacoes: obs
                })
                if (!firstId) firstId = cp?.id || null
              }
              financeiroTipo = 'contas_pagar'
              financeiroId = firstId
            } else {
              const existing = await agrogestao.entities.ContaPagar.filter({ descricao: descBase, data_vencimento: venc, categoria: 'Compra de Animais' }, "-data_vencimento", 5)
              const reuse = (existing || []).find(e => Number(e.valor || 0) === valor)
              if (reuse) {
                financeiroTipo = 'contas_pagar'
                financeiroId = reuse.id
              } else {
                const cp = await agrogestao.entities.ContaPagar.create({
                  propriedade_id: propriedadeId,
                  descricao: descBase,
                  valor: valor,
                  data_vencimento: venc,
                  categoria: 'Compra de Animais',
                  fornecedor: moveData.fornecedor || '',
                  status: 'pendente',
                  observacoes: obs
                })
                financeiroTipo = 'contas_pagar'
                financeiroId = cp?.id || null
              }
            }
          } else if (tipo === 'obito') {
            financeiroTipo = 'lancamentos'
            financeiroId = financeiroObitoId
          }
        }
        const u = await agrogestao.auth.me()
        const hoje = new Date().toISOString().split('T')[0]
        await agrogestao.entities.MovimentoAnimal.create({
          propriedade_id: propriedadeId,
          animal_id: it.id,
          tipo,
          quantidade: qtd,
          valor: valor || null,
          operacao: moveData.operacao || null,
          financeiro_tipo: financeiroTipo,
          financeiro_id: financeiroId,
          lote_origem: it.lote || null,
          lote_destino: moveData.lote_destino || null,
          observacoes: (tipo === 'venda' || tipo === 'compra') ? `Cond.: ${moveData.condicao_pagamento}${moveData.condicao_pagamento === 'parcelado' ? ` • ${parseInt(moveData.parcelas || 0)}x ${moveData.frequencia}` : ''}${moveData.finalidade ? ` • Finalidade: ${moveData.finalidade}` : ''}` : (moveData.finalidade || null),
          data: hoje,
          created_by: u?.email || null
        })
      }
      setMoveDialog(null)
      setMoveData({ tipo: 'venda', quantidade: '', valor: '', condicao_pagamento: 'avista', parcelas: '', data_vencimento: new Date().toISOString().split('T')[0], frequencia: 'mensal', cliente: '', fornecedor: '', finalidade: '' })
      setSelectedIds([])
      setMultiSelect(false)
      queryClient.invalidateQueries({ queryKey: ["animais", propriedadeId] })
      queryClient.invalidateQueries({ queryKey: ["contas_receber_prop", propriedadeId] })
      queryClient.invalidateQueries({ queryKey: ["contas_pagar_prop", propriedadeId] })
      queryClient.invalidateQueries({ queryKey: ["contas_receber", propriedadeId] })
      queryClient.invalidateQueries({ queryKey: ["contas_pagar", propriedadeId] })
    } catch (err) {
      setErrorMsg(err?.message || "Erro ao salvar movimentação")
    } finally {
      setSavingMove(false)
    }
  }

  const isMoveValid = useMemo(() => {
    if (!moveDialog) return false
    const q = parseInt(moveData.quantidade || 0)
    if (!q || q <= 0) return false
    if (moveData.tipo === 'transferencia') return !!moveData.lote_destino
    if (moveData.tipo === 'ajuste') return true
    if (moveData.tipo === 'venda' || moveData.tipo === 'compra') {
      const v = parseFloat(moveData.valor || 0)
      const hasParty = moveData.tipo === 'venda' ? !!String(moveData.cliente || '').trim() : !!String(moveData.fornecedor || '').trim()
      const hasDue = !!String(moveData.data_vencimento || '').trim()
      const parcelasOk = moveData.condicao_pagamento !== 'parcelado' ? true : (parseInt(moveData.parcelas || 0) > 0)
      return v > 0 && hasParty && hasDue && parcelasOk
    }
    if (moveData.tipo === 'obito') return true
    return false
  }, [moveDialog, moveData])

  if (!propriedadeId) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Selecione uma propriedade</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-stone-700 mb-4">Para gerenciar o rebanho, selecione primeiro a propriedade em Propriedades.</p>
            <a href="/Propriedades" className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg">Ir para Propriedades</a>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Tractor className="w-8 h-8 text-[#40916C] animate-bounce" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-stone-800">Rebanho</h1>
          <p className="text-stone-600 mt-1">Controle de pecuária leiteira, corte e demais animais</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="space-y-1">
            <Label>Categoria</Label>
            <Select value={filterCategoria} onValueChange={(v) => setFilterCategoria(v === 'todas' ? '' : v)}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                {CATEGORIAS.map(c => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Buscar</Label>
            <Input placeholder="Lote ou observação" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1) }} className="w-56" />
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => resetForm()} className="bg-[#1B4332] hover:bg-[#2D6A4F] gap-2">
              <Plus className="w-5 h-5" />
              Novo Registro
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingId ? "Editar Registro" : "Novo Registro"}</DialogTitle>
              <DialogDescription>Cadastre um grupo/lote de animais</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label>Categoria *</Label>
                <Select value={formData.categoria} onValueChange={(v) => setFormData({ ...formData, categoria: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIAS.map(c => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quantidade *</Label>
                  <Input type="number" value={formData.quantidade} onChange={(e) => setFormData({ ...formData, quantidade: e.target.value })} required />
                  <div className="text-xs text-stone-500">Número de cabeças no lote</div>
                </div>
                <div className="space-y-2">
                  <Label>Lote</Label>
                  <Input value={formData.lote} onChange={(e) => setFormData({ ...formData, lote: e.target.value })} />
                  <div className="text-xs text-stone-500">Identificação do lote (ex.: Lote A, Piquete 3)</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Peso médio (kg)</Label>
                  <Input type="number" step="0.1" value={formData.peso_medio} onChange={(e) => setFormData({ ...formData, peso_medio: e.target.value })} />
                  <div className="text-xs text-stone-500">Peso médio por cabeça</div>
                </div>
                <div className="space-y-2">
                  <Label>Data de entrada *</Label>
                  <Input type="date" value={formData.data_entrada} onChange={(e) => setFormData({ ...formData, data_entrada: e.target.value })} required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de custo</Label>
                  <Select value={formData.custo_tipo} onValueChange={(v) => setFormData({ ...formData, custo_tipo: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="total">Total do lote</SelectItem>
                      <SelectItem value="unitario">Unitário por cabeça</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>
                    {`Custo de aquisição (${formData.custo_tipo === 'unitario' ? 'unitário' : 'total'}) (R$)`}
                  </Label>
                  <Input type="number" step="0.01" value={formData.custo_aquisicao} onChange={(e) => setFormData({ ...formData, custo_aquisicao: e.target.value })} />
                  <div className="text-xs text-stone-500">
                    {formData.custo_tipo === 'unitario' 
                      ? 'Informe o valor por cabeça; o total será calculado'
                      : 'Informe o valor total referente ao lote'}
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Observações</Label>
                <Input value={formData.observacoes} onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                <Button type="submit" className="bg-[#40916C] hover:bg-[#2D6A4F] disabled:opacity-50" disabled={savingAnimal}>
                  {savingAnimal ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" className="opacity-25" />
                        <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" className="opacity-75" />
                      </svg>
                      Salvando...
                    </span>
                  ) : "Salvar"}
                </Button>
              </div>
              {errorMsg ? (<div className="text-red-600 text-sm pt-2">{errorMsg}</div>) : null}
            </form>
          </DialogContent>
          </Dialog>
          <ExportacaoRelatorios
            dados={animaisFiltrados}
            nomeArquivo="rebanho"
            colunas={[
              { label: 'Categoria', accessor: (a) => CATEGORIAS.find(c => c.value === a.categoria)?.label || a.categoria },
              { label: 'Lote', accessor: (a) => a.lote || '-' },
              { label: 'Quantidade', accessor: (a) => a.quantidade || 0 },
              { label: 'Peso Médio (kg)', accessor: (a) => typeof a.peso_medio === 'number' ? a.peso_medio.toFixed(1) : (a.peso_medio || '-') },
              { label: 'Custo Total (R$)', accessor: (a) => typeof a.custo_aquisicao === 'number' ? a.custo_aquisicao : '-', type: 'currency' },
              { label: 'Custo/Cabeça (R$)', accessor: (a) => (typeof a.custo_aquisicao === 'number' && typeof a.quantidade === 'number' && a.quantidade > 0) ? (a.custo_aquisicao / a.quantidade) : '-', type: 'currency' },
            ]}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-[#D4A373] to-[#BC8A5F] text-white shadow-lg">
          <CardContent className="p-4">
            <p className="text-xs/5 opacity-80">Grupos</p>
            <p className="text-2xl font-bold">{formatNumber(totalGrupos)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-[#40916C] to-[#2D6A4F] text-white shadow-lg">
          <CardContent className="p-4">
            <p className="text-xs/5 opacity-80">Cabeças</p>
            <p className="text-2xl font-bold">{formatNumber(totalCabecas)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-lg">
          <CardContent className="p-4">
            <p className="text-xs/5 opacity-80">Leite</p>
            <p className="text-2xl font-bold">{formatNumber(totalLeite)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500 to-amber-700 text-white shadow-lg">
          <CardContent className="p-4">
            <p className="text-xs/5 opacity-80">Corte</p>
            <p className="text-2xl font-bold">{formatNumber(totalCorte)}</p>
          </CardContent>
        </Card>
      </div>

      <RebanhoChart data={chartData} />

      <Card>
        <CardContent className="p-4">
          {animaisFiltrados.length === 0 ? (
            <div className="text-stone-600">Nenhum registro de rebanho.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <AnimatePresence>
                {animaisPagina.map((a) => {
                  const cls = categoriaClasses(a.categoria)
                  return (
                  <motion.div key={a.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className={`border rounded-xl p-4 bg-white border-l-4 ${cls.border}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium text-stone-800">
                        {CATEGORIAS.find(c => c.value === a.categoria)?.label || a.categoria}
                      </div>
                      <Badge variant="secondary" className={`${cls.badge}`}>{a.lote || 'Sem lote'}</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="p-2 bg-stone-50 rounded">
                        <p className="text-stone-500 text-xs">Quantidade</p>
                        <p className="font-semibold text-stone-800">{formatNumber(a.quantidade)}</p>
                      </div>
                      <div className="p-2 bg-stone-50 rounded">
                        <p className="text-stone-500 text-xs">Peso médio (kg)</p>
                        <p className="font-semibold text-stone-800">{typeof a.peso_medio === 'number' ? new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(a.peso_medio) : (a.peso_medio || '-')}</p>
                      </div>
                      <div className="p-2 bg-stone-50 rounded">
                        <p className="text-stone-500 text-xs">Custo aquisição (R$)</p>
                        <p className="font-semibold text-stone-800">{formatCurrency(a.custo_aquisicao)}</p>
                      </div>
                      <div className="p-2 bg-stone-50 rounded">
                        <p className="text-stone-500 text-xs">Custo por cabeça (R$)</p>
                        <p className="font-semibold text-stone-800">{typeof a.custo_aquisicao === 'number' && typeof a.quantidade === 'number' && a.quantidade > 0 ? formatCurrency(a.custo_aquisicao / a.quantidade) : '-'}</p>
                      </div>
                      <div className="p-2 bg-stone-50 rounded col-span-2">
                        <p className="text-stone-500 text-xs">Entrada</p>
                        <p className="font-semibold text-stone-800">{formatDate(a.data_entrada)}</p>
                      </div>
                      {a.observacoes ? (
                        <div className="p-2 bg-stone-50 rounded col-span-2">
                          <p className="text-stone-500 text-xs">Observações</p>
                          <p className="text-stone-700">{String(a.observacoes).slice(0, 120)}</p>
                        </div>
                      ) : null}
                    </div>
                    {inlineEditId === a.id ? (
                      <div className="grid grid-cols-2 gap-2 mt-3">
                        <div className="space-y-1">
                          <Label>Quantidade</Label>
                          <Input type="number" value={inlineEdit.quantidade} onChange={(e) => setInlineEdit({ ...inlineEdit, quantidade: e.target.value })} />
                        </div>
                        <div className="space-y-1">
                          <Label>Peso médio (kg)</Label>
                          <Input type="number" step="0.1" value={inlineEdit.peso_medio} onChange={(e) => setInlineEdit({ ...inlineEdit, peso_medio: e.target.value })} />
                        </div>
                        <div className="col-span-2 flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => { setInlineEditId(null) }}>Cancelar</Button>
                          <Button size="sm" className="bg-[#40916C] hover:bg-[#2D6A4F]" onClick={() => updateMutation.mutate({ id: a.id, data: { ...a, quantidade: inlineEdit.quantidade, peso_medio: inlineEdit.peso_medio } })}>Salvar</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-end gap-2 mt-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-700">
                            <Menu className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={() => { setMoveDialog(a); setMoveData({ ...moveData, tipo: 'transferencia' }) }}>Transferência</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setMoveDialog(a); setMoveData({ ...moveData, tipo: 'venda' }) }}>Venda</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setMoveDialog(a); setMoveData({ ...moveData, tipo: 'compra' }) }}>Compra</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setMoveDialog(a); setMoveData({ ...moveData, tipo: 'ajuste', operacao: 'entrada' }) }}>Ajuste (Entrada)</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setMoveDialog(a); setMoveData({ ...moveData, tipo: 'ajuste', operacao: 'saida' }) }}>Ajuste (Saída)</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Button variant="ghost" size="sm" onClick={() => setHistDialog(a)} className="text-stone-400 hover:text-stone-600">
                        <Clock className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteMutation.mutate(a.id)} className="text-stone-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    )}
                  </motion.div>
                )
              })
              }
              </AnimatePresence>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Relatórios e KPIs</CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-4">
          <div>
            <div className="text-stone-700 font-medium mb-2">Composição por categoria</div>
            <div className="space-y-2">
              {porCategoria.map(pc => {
                const pct = totalCabecas ? Math.round((pc.qtd / totalCabecas) * 100) : 0
                return (
                  <div key={pc.cat} className="grid grid-cols-6 items-center gap-2">
                    <div className="col-span-1 text-sm text-stone-700">{pc.label}</div>
                    <div className="col-span-4 h-2 bg-stone-100 rounded">
                      <div className="h-2 bg-[#2D6A4F] rounded" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="col-span-1 text-right text-sm text-stone-700">{formatNumber(pc.qtd)}</div>
                  </div>
                )
              })}
            </div>
          </div>

          <div>
            <div className="text-stone-700 font-medium mb-2">Evolução mensal de cabeças</div>
            <div className="grid grid-cols-12 gap-2 items-end">
              {evolucaoMensal.map(m => (
                <div key={m.key} className="text-center">
                  <div className="w-6 mx-auto bg-emerald-600 rounded" style={{ height: `${Math.min(100, Math.max(4, Math.round((m.total / (totalCabecas || 1)) * 100)))}px` }} />
                  <div className="text-[10px] text-stone-500 mt-1">{m.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-rose-500 to-rose-700 text-white">
              <CardContent className="p-4">
                <div className="text-xs/5 opacity-80">Taxa de mortalidade (30d)</div>
                <div className="text-2xl font-bold">{(taxaObito30 * 100).toFixed(1)}%</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-[#40916C] to-[#2D6A4F] text-white">
              <CardContent className="p-4">
                <div className="text-xs/5 opacity-80">Compras (30d)</div>
                <div className="text-2xl font-bold">{formatNumber(compras30)}</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-500 to-amber-700 text-white">
              <CardContent className="p-4">
                <div className="text-xs/5 opacity-80">Vendas (30d)</div>
                <div className="text-2xl font-bold">{formatNumber(vendas30)}</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-[#D4A373] to-[#BC8A5F] text-white">
              <CardContent className="p-4">
                <div className="text-xs/5 opacity-80">Custo médio por cabeça (R$)</div>
                <div className="text-2xl font-bold">{formatCurrency(animais.reduce((acc,a)=>acc+(typeof a.custo_aquisicao==='number'?a.custo_aquisicao:0),0)/(totalCabecas||1))}</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-indigo-500 to-indigo-700 text-white">
              <CardContent className="p-4">
                <div className="text-xs/5 opacity-80">Receita vendas (30d)</div>
                <div className="text-2xl font-bold">{formatCurrency(receitaVendas30)}</div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-red-500 to-red-700 text-white">
              <CardContent className="p-4">
                <div className="text-xs/5 opacity-80">Custo compras (30d)</div>
                <div className="text-2xl font-bold">{formatCurrency(custoCompras30)}</div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {animaisFiltrados.length > pageSize && (
        <Paginacao
          totalItems={animaisFiltrados.length}
          currentPage={currentPage}
          onPageChange={(p) => setCurrentPage(p)}
          pageSize={pageSize}
          onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1) }}
        />
      )}

      <Dialog open={!!moveDialog} onOpenChange={(o) => { if (!o) { setMoveDialog(null); setMoveTab("dados") } }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${moveData.tipo === 'venda' ? 'bg-blue-100 text-blue-600' : moveData.tipo === 'compra' ? 'bg-emerald-100 text-emerald-600' : moveData.tipo === 'obito' ? 'bg-red-100 text-red-600' : 'bg-stone-100 text-stone-600'}`}>
                {moveData.tipo === 'venda' ? <DollarSign className="w-6 h-6" /> :
                 moveData.tipo === 'compra' ? <Package className="w-6 h-6" /> :
                 moveData.tipo === 'obito' ? <AlertCircle className="w-6 h-6" /> :
                 moveData.tipo === 'transferencia' ? <ArrowRightLeft className="w-6 h-6" /> : <Info className="w-6 h-6" />}
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold">Movimentar Rebanho</DialogTitle>
                <DialogDescription>
                  {moveDialog?.lote ? `Lote selecionado: ${moveDialog.lote}` : 'Novo registro de movimentação'}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {moveDialog && (
            <Tabs value={moveTab} onValueChange={setMoveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="dados">1. Dados Básicos</TabsTrigger>
                <TabsTrigger value="financeiro" disabled={!['compra', 'venda'].includes(moveData.tipo)}>2. Financeiro</TabsTrigger>
                <TabsTrigger value="resumo">3. Confirmação</TabsTrigger>
              </TabsList>

              <div className="space-y-4">
                <TabsContent value="dados" className="space-y-4 animate-in fade-in-50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="border-none shadow-none bg-stone-50">
                      <CardContent className="p-4 space-y-4">
                        <div className="space-y-2">
                          <Label>Tipo de Operação</Label>
                          <Select value={moveData.tipo} onValueChange={(v) => setMoveData({ ...moveData, tipo: v })}>
                            <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="venda">Venda (Saída)</SelectItem>
                              <SelectItem value="compra">Compra (Entrada)</SelectItem>
                              <SelectItem value="obito">Óbito (Perda)</SelectItem>
                              <SelectItem value="transferencia">Transferência</SelectItem>
                              <SelectItem value="ajuste">Ajuste de Estoque</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Finalidade</Label>
                          <Select value={moveData.finalidade || ''} onValueChange={(v) => setMoveData({ ...moveData, finalidade: v })}>
                            <SelectTrigger className="bg-white"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="reposicao">Reposição</SelectItem>
                              <SelectItem value="descarte">Descarte</SelectItem>
                              <SelectItem value="negocio">Negócio</SelectItem>
                              <SelectItem value="sanitario">Sanitário</SelectItem>
                              <SelectItem value="nascimento">Nascimento</SelectItem>
                              <SelectItem value="outro">Outro</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-none shadow-none bg-stone-50">
                      <CardContent className="p-4 space-y-4">
                        <div className="space-y-2">
                          <Label>Quantidade *</Label>
                          <div className="relative">
                            <Input 
                              type="number" 
                              value={moveData.quantidade} 
                              onChange={(e) => setMoveData({ ...moveData, quantidade: e.target.value })} 
                              className="pl-9 bg-white text-lg font-medium" 
                              placeholder="0"
                            />
                            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">#</div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Data do Evento</Label>
                          <div className="relative">
                            <Input 
                              type="date" 
                              value={moveData.data_vencimento ? moveData.data_vencimento.split('T')[0] : new Date().toISOString().split('T')[0]} 
                              onChange={(e) => setMoveData({...moveData, data_vencimento: e.target.value})} 
                              className="pl-9 bg-white"
                            />
                            <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {moveData.tipo === 'transferencia' && (
                    <div className="space-y-2 p-4 bg-blue-50 rounded-lg border border-blue-100">
                      <Label className="text-blue-700 font-medium">Lote de Destino</Label>
                      <Input 
                        placeholder="Digite o nome do lote destino..." 
                        value={moveData.lote_destino || ''} 
                        onChange={(e) => setMoveData({ ...moveData, lote_destino: e.target.value })} 
                        className="bg-white"
                      />
                    </div>
                  )}

                  <div className="pt-2">
                    <div className="flex items-center gap-2 mb-2">
                       <input 
                         type="checkbox" 
                         id="multiSelect" 
                         checked={multiSelect} 
                         onChange={(e) => setMultiSelect(e.target.checked)} 
                         className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                       />
                       <Label htmlFor="multiSelect" className="cursor-pointer">Selecionar múltiplos animais/lotes para esta operação</Label>
                    </div>
                    
                    {multiSelect && (
                      <Card className="border shadow-sm">
                        <CardContent className="p-3">
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Filtro Categoria</Label>
                              <Select value={filterMultiCat} onValueChange={(v) => setFilterMultiCat(v)}>
                                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Todas" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="">Todas</SelectItem>
                                  {CATEGORIAS.map(c => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Buscar Lote</Label>
                              <Input className="h-8 text-xs" value={filterMultiLote} onChange={(e) => setFilterMultiLote(e.target.value)} placeholder="Digite para filtrar..." />
                            </div>
                          </div>
                          <div className="max-h-40 overflow-y-auto border rounded-md p-2 bg-stone-50">
                            {animais
                              .filter(a => (!filterMultiCat || a.categoria === filterMultiCat))
                              .filter(a => (!filterMultiLote || String(a.lote || '').toLowerCase().includes(filterMultiLote.toLowerCase())))
                              .map(a => (
                              <label key={a.id} className="flex items-center gap-2 py-1.5 px-2 hover:bg-stone-100 rounded cursor-pointer transition-colors">
                                <input type="checkbox" checked={selectedIds.includes(a.id)} onChange={(e) => {
                                  const checked = e.target.checked
                                  setSelectedIds(prev => checked ? [...prev, a.id] : prev.filter(x => x !== a.id))
                                }} className="rounded border-stone-300 text-emerald-600" />
                                <div className="text-xs">
                                  <span className="font-medium text-stone-700">{a.lote || 'Sem lote'}</span>
                                  <span className="text-stone-500 mx-1">•</span>
                                  <span className="text-stone-500">{CATEGORIAS.find(c => c.value === a.categoria)?.label || a.categoria}</span>
                                  <span className="text-stone-500 mx-1">•</span>
                                  <Badge variant="outline" className="text-[10px] h-5 px-1">{a.quantidade} cab</Badge>
                                </div>
                              </label>
                            ))}
                          </div>
                          <div className="text-xs text-stone-500 mt-2 text-right">{selectedIds.length} itens selecionados</div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="financeiro" className="space-y-4 animate-in fade-in-50">
                   <div className="p-4 bg-stone-50 rounded-lg border">
                      <div className="grid grid-cols-2 gap-4">
                         <div className="space-y-2">
                            <Label>Valor Total (R$)</Label>
                            <div className="relative">
                              <Input 
                                type="number" 
                                step="0.01" 
                                value={moveData.valor} 
                                onChange={(e) => setMoveData({ ...moveData, valor: e.target.value })} 
                                className="pl-9 font-medium text-lg bg-white" 
                                placeholder="0.00"
                              />
                              <DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
                            </div>
                         </div>
                         <div className="space-y-2">
                            <Label>Valor Unitário (Estimado)</Label>
                            <div className="h-10 px-3 flex items-center bg-white border rounded-md text-stone-600 text-sm font-medium">
                               R$ {moveData.quantidade > 0 ? (moveData.valor / moveData.quantidade).toFixed(2) : '0.00'} / animal
                            </div>
                         </div>
                      </div>
                   </div>

                   <div className="space-y-2">
                      <Label>{moveData.tipo === 'venda' ? 'Cliente / Comprador' : 'Fornecedor / Vendedor'}</Label>
                      <div className="bg-white p-1 rounded-md border">
                        {moveData.tipo === 'venda' ? (
                          <PartnerSelect tipo="cliente" value={moveData.cliente} onChange={(v) => setMoveData({ ...moveData, cliente: v })} />
                        ) : (
                          <PartnerSelect tipo="fornecedor" value={moveData.fornecedor} onChange={(v) => setMoveData({ ...moveData, fornecedor: v })} />
                        )}
                      </div>
                   </div>

                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Condição de Pagamento</Label>
                        <Select value={moveData.condicao_pagamento} onValueChange={(v) => setMoveData({ ...moveData, condicao_pagamento: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="avista">À vista</SelectItem>
                            <SelectItem value="parcelado">Parcelado</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {moveData.condicao_pagamento === 'parcelado' && (
                        <div className="space-y-2 animate-in slide-in-from-top-2">
                          <Label>Nº Parcelas</Label>
                          <Input type="number" value={moveData.parcelas} onChange={(e) => setMoveData({ ...moveData, parcelas: e.target.value })} placeholder="Ex: 3" />
                        </div>
                      )}
                      {moveData.condicao_pagamento === 'parcelado' && (
                          <div className="space-y-2 animate-in slide-in-from-top-2">
                            <Label>Frequência</Label>
                            <Select value={moveData.frequencia} onValueChange={(v) => setMoveData({ ...moveData, frequencia: v })}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="mensal">Mensal</SelectItem>
                                <SelectItem value="semanal">Semanal</SelectItem>
                                <SelectItem value="quinzenal">Quinzenal</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                      )}
                   </div>
                </TabsContent>

                <TabsContent value="resumo" className="space-y-4 animate-in fade-in-50">
                  <Card className="border-emerald-100 bg-emerald-50/30">
                     <CardHeader className="pb-2 border-b border-emerald-100">
                        <CardTitle className="text-base flex items-center gap-2 text-emerald-800">
                           <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Resumo da Operação
                        </CardTitle>
                     </CardHeader>
                     <CardContent className="space-y-3 text-sm pt-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <span className="text-stone-500 block text-xs uppercase tracking-wider">Operação</span>
                            <span className="font-semibold text-lg capitalize text-stone-800">{moveData.tipo}</span>
                          </div>
                          <div>
                            <span className="text-stone-500 block text-xs uppercase tracking-wider">Quantidade</span>
                            <span className="font-semibold text-lg text-stone-800">{moveData.quantidade} <span className="text-sm font-normal text-stone-600">animais</span></span>
                          </div>
                        </div>

                        {['compra', 'venda'].includes(moveData.tipo) && (
                           <div className="mt-4 pt-4 border-t border-emerald-100/50">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                   <span className="text-stone-500 block text-xs uppercase tracking-wider">Valor Total</span>
                                   <span className="font-bold text-lg text-emerald-700">R$ {parseFloat(moveData.valor || 0).toLocaleString('pt-BR', {minimumFractionDigits: 2})}</span>
                                </div>
                                <div>
                                   <span className="text-stone-500 block text-xs uppercase tracking-wider">Parceiro</span>
                                   <span className="font-medium text-stone-800 truncate block">
                                      {moveData.tipo === 'venda' 
                                        ? (moveData.cliente ? 'Cliente Selecionado' : 'Não informado')
                                        : (moveData.fornecedor ? 'Fornecedor Selecionado' : 'Não informado')}
                                   </span>
                                </div>
                              </div>
                           </div>
                        )}
                        
                        {multiSelect && selectedIds.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-emerald-100/50">
                            <span className="text-stone-500 block text-xs uppercase tracking-wider mb-1">Itens Afetados</span>
                            <div className="flex flex-wrap gap-1">
                              {selectedIds.slice(0, 5).map(id => (
                                <Badge key={id} variant="secondary" className="text-[10px] bg-white border-stone-200">ID: {id.slice(0,4)}...</Badge>
                              ))}
                              {selectedIds.length > 5 && <Badge variant="secondary" className="text-[10px] bg-white">+{selectedIds.length - 5}</Badge>}
                            </div>
                          </div>
                        )}
                     </CardContent>
                  </Card>

                  <div className="flex items-start gap-3 p-4 bg-blue-50 text-blue-800 rounded-lg text-sm border border-blue-100">
                     <Info className="w-5 h-5 mt-0.5 shrink-0" />
                     <div className="space-y-1">
                       <p className="font-medium">Atenção</p>
                       <p className="opacity-90">Esta ação irá atualizar o estoque imediatamente e gerar os lançamentos financeiros correspondentes (contas a {moveData.tipo === 'venda' ? 'receber' : 'pagar'}).</p>
                     </div>
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          )}

          <DialogFooter className="flex flex-row justify-between sm:justify-between items-center gap-2 mt-4 pt-4 border-t">
            <Button variant="ghost" onClick={() => setMoveDialog(null)}>Cancelar</Button>
            <div className="flex gap-2">
               {moveTab !== 'dados' && (
                  <Button variant="outline" onClick={() => setMoveTab(moveTab === 'resumo' ? (['compra', 'venda'].includes(moveData.tipo) ? 'financeiro' : 'dados') : 'dados')}>
                    Voltar
                  </Button>
               )}
               {moveTab === 'resumo' ? (
                  <Button className="bg-emerald-600 hover:bg-emerald-700 min-w-[140px]" onClick={handleMovimentar} disabled={!isMoveValid || savingMove}>
                     {savingMove ? (
                        <>
                           <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                           Processando...
                        </>
                     ) : (
                        isMoveValid ? 'Confirmar' : 'Preencha os dados'
                     )}
                  </Button>
               ) : (
                  <Button onClick={() => {
                    if (moveTab === 'dados') {
                      if (['compra', 'venda'].includes(moveData.tipo)) setMoveTab('financeiro')
                      else setMoveTab('resumo')
                    } else {
                      setMoveTab('resumo')
                    }
                  }}>
                     Próximo
                  </Button>
               )}
            </div>
          </DialogFooter>
          {errorMsg ? (<div className="text-red-600 text-sm text-center bg-red-50 p-2 rounded mt-2">{errorMsg}</div>) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={!!histDialog} onOpenChange={(o) => { if (!o) { setHistDialog(null) } }}>
        <DialogContent className="sm:max-w-4xl h-[90vh] sm:h-[80vh] flex flex-col p-0 gap-0 overflow-hidden bg-stone-50/50">
          <div className="p-6 bg-white border-b sticky top-0 z-10">
             <DialogHeader className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                   <div className="space-y-1">
                      <DialogTitle className="text-2xl font-bold text-stone-900 flex items-center gap-2">
                         <div className={`p-2 rounded-lg ${categoriaClasses(histDialog?.categoria).badge} bg-opacity-20`}>
                            {histDialog?.categoria === 'bovino_leite' ? <Milk className="w-5 h-5" /> : 
                             histDialog?.categoria === 'aves' ? <Package className="w-5 h-5" /> : 
                             <Tractor className="w-5 h-5" />}
                         </div>
                         {histDialog?.lote || 'Lote sem nome'}
                      </DialogTitle>
                      <DialogDescription className="text-base">
                         {CATEGORIAS.find(c => c.value === histDialog?.categoria)?.label} • ID: {histDialog?.id?.slice(0,8)}
                      </DialogDescription>
                   </div>
                   <div className="flex gap-3 items-center">
                      <div className="px-4 py-2 bg-stone-100 rounded-lg border border-stone-200 text-center min-w-[100px]">
                         <span className="text-xs text-stone-500 uppercase font-semibold block">Quantidade</span>
                         <span className="text-xl font-bold text-stone-900">{histDialog?.quantidade || 0}</span>
                      </div>
                      <div className="px-4 py-2 bg-emerald-50 rounded-lg border border-emerald-100 text-center min-w-[100px]">
                         <span className="text-xs text-emerald-600 uppercase font-semibold block">Peso Médio</span>
                         <span className="text-xl font-bold text-emerald-800">{histDialog?.peso_medio || 0} kg</span>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => setHistDialog(null)} className="ml-2 text-stone-400 hover:text-stone-600">
                        <X className="w-6 h-6" />
                      </Button>
                   </div>
                </div>
             </DialogHeader>
          </div>

          {histDialog && (
            <Tabs defaultValue="movimentacoes" className="flex-1 flex flex-col overflow-hidden">
               <div className="px-6 bg-white border-b shadow-sm z-10">
                 <TabsList className="h-12 w-full justify-start bg-transparent p-0 gap-8 overflow-x-auto no-scrollbar">
                   <TabsTrigger value="movimentacoes" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-indigo-600 rounded-none h-full px-0 font-medium text-stone-500 data-[state=active]:text-indigo-600 transition-none">
                      <ArrowRightLeft className="w-4 h-4 mr-2" /> Movimentações
                   </TabsTrigger>
                   <TabsTrigger value="pesagens" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-indigo-600 rounded-none h-full px-0 font-medium text-stone-500 data-[state=active]:text-indigo-600 transition-none">
                      <Scale className="w-4 h-4 mr-2" /> Pesagens & Evolução
                   </TabsTrigger>
                   <TabsTrigger value="manejos" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-indigo-600 rounded-none h-full px-0 font-medium text-stone-500 data-[state=active]:text-indigo-600 transition-none">
                      <Syringe className="w-4 h-4 mr-2" /> Manejo Sanitário
                   </TabsTrigger>
                   {histDialog?.categoria === 'bovino_leite' && (
                     <TabsTrigger value="producao" className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-indigo-600 rounded-none h-full px-0 font-medium text-stone-500 data-[state=active]:text-indigo-600 transition-none">
                        <Milk className="w-4 h-4 mr-2" /> Produção de Leite
                     </TabsTrigger>
                   )}
                 </TabsList>
               </div>

               <div className="flex-1 overflow-y-auto p-6 bg-stone-50/50">
                  <TabsContent value="movimentacoes" className="mt-0 space-y-6 h-full">
                     {isHistLoading ? (
                        <div className="flex items-center justify-center h-40"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
                     ) : historicos.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-stone-400">
                           <ArrowRightLeft className="w-12 h-12 mb-4 opacity-20" />
                           <p>Nenhuma movimentação registrada</p>
                        </div>
                     ) : (
                        <div className="relative space-y-0 pl-4 border-l-2 border-stone-200 ml-4">
                           {historicos.map((h, i) => (
                              <div key={h.id} className="relative pb-8 last:pb-0">
                                 <span className={`absolute -left-[21px] top-1 w-10 h-10 rounded-full border-4 border-stone-50 flex items-center justify-center bg-white shadow-sm ${
                                    h.tipo === 'compra' ? 'text-emerald-600' : 
                                    h.tipo === 'venda' ? 'text-blue-600' : 
                                    h.tipo === 'obito' ? 'text-red-600' : 'text-stone-600'
                                 }`}>
                                    {h.tipo === 'compra' ? <Package className="w-4 h-4" /> :
                                     h.tipo === 'venda' ? <DollarSign className="w-4 h-4" /> :
                                     h.tipo === 'obito' ? <AlertCircle className="w-4 h-4" /> :
                                     <ArrowRightLeft className="w-4 h-4" />}
                                 </span>
                                 <Card className="ml-6 border-stone-100 shadow-sm hover:shadow-md transition-shadow">
                                    <CardContent className="p-4">
                                       <div className="flex justify-between items-start mb-2">
                                          <div>
                                             <h4 className="font-semibold text-stone-900 capitalize">{h.tipo}</h4>
                                             <p className="text-sm text-stone-500">{formatDate(h.data)}</p>
                                          </div>
                                          <Badge variant="outline" className={`${
                                             h.tipo === 'compra' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 
                                             h.tipo === 'venda' ? 'bg-blue-50 text-blue-700 border-blue-200' : 
                                             'bg-stone-50 text-stone-700'
                                          }`}>
                                             {h.quantidade} animais
                                          </Badge>
                                       </div>
                                       {h.valor && (
                                          <div className="pt-2 border-t border-stone-50 mt-2 flex justify-between items-center text-sm">
                                             <span className="text-stone-500">Valor Total</span>
                                             <span className="font-medium text-stone-900">R$ {parseFloat(h.valor).toFixed(2)}</span>
                                          </div>
                                       )}
                                    </CardContent>
                                 </Card>
                              </div>
                           ))}
                        </div>
                     )}
                  </TabsContent>

                  <TabsContent value="pesagens" className="mt-0 space-y-6">
                     <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2 space-y-6">
                           <Card className="border-none shadow-sm">
                              <CardHeader>
                                 <CardTitle className="text-lg flex items-center gap-2">
                                    <LineChart className="w-5 h-5 text-indigo-600" /> Histórico de Peso
                                 </CardTitle>
                              </CardHeader>
                              <CardContent>
                                 {isPesLoading ? (
                                    <div className="h-40 flex items-center justify-center"><Loader2 className="animate-spin text-stone-400" /></div>
                                 ) : pesagens.length === 0 ? (
                                    <div className="text-center text-stone-500 py-8">Nenhuma pesagem registrada</div>
                                 ) : (
                                    <div className="space-y-4">
                                       {pesagens.map(p => (
                                          <div key={p.id} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-100">
                                             <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xs">
                                                   {new Date(p.data).getDate()}
                                                </div>
                                                <div>
                                                   <p className="font-medium text-stone-900">{formatDate(p.data)}</p>
                                                   <p className="text-xs text-stone-500">Pesagem de rotina</p>
                                                </div>
                                             </div>
                                             <div className="text-right">
                                                <span className="text-lg font-bold text-stone-900">{p.peso_medio} kg</span>
                                             </div>
                                          </div>
                                       ))}
                                    </div>
                                 )}
                              </CardContent>
                           </Card>
                        </div>
                        <div className="space-y-6">
                           <Card className="bg-indigo-50 border-indigo-100">
                              <CardHeader className="pb-2">
                                 <CardTitle className="text-base text-indigo-900">Nova Pesagem</CardTitle>
                              </CardHeader>
                              <CardContent className="space-y-4">
                                 <p className="text-sm text-indigo-700">Registre o peso médio atual do lote para acompanhar o ganho de peso.</p>
                                 <Button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" onClick={async () => {
                                    const u = await agrogestao.auth.me()
                                    await agrogestao.entities.PesagemAnimal.create({
                                      propriedade_id: propriedadeId,
                                      animal_id: histDialog.id,
                                      data: new Date().toISOString().split('T')[0],
                                      peso_medio: histDialog.peso_medio || 0,
                                      observacoes: null,
                                      created_by: u?.email || null
                                    })
                                    queryClient.invalidateQueries({ queryKey: ["pesagens_animais", histDialog.id] })
                                 }}>
                                    <Scale className="w-4 h-4 mr-2" /> Registrar Peso Atual
                                 </Button>
                              </CardContent>
                           </Card>
                        </div>
                     </div>
                  </TabsContent>

                  <TabsContent value="manejos" className="mt-0 space-y-6">
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                           <h3 className="font-semibold text-stone-800 flex items-center gap-2"><Clock className="w-5 h-5 text-orange-500" /> Próximos Eventos</h3>
                           {manejos.filter(m => m.proxima_data && new Date(m.proxima_data) >= new Date()).length === 0 ? (
                              <Card className="bg-stone-50 border-dashed"><CardContent className="p-6 text-center text-stone-500">Nenhum evento futuro agendado</CardContent></Card>
                           ) : (
                              manejos.filter(m => m.proxima_data && new Date(m.proxima_data) >= new Date()).map(m => (
                                 <Card key={m.id} className="border-l-4 border-l-orange-500">
                                    <CardContent className="p-4">
                                       <div className="flex justify-between">
                                          <span className="font-medium text-stone-900 capitalize">{m.tipo}</span>
                                          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">{formatDate(m.proxima_data)}</Badge>
                                       </div>
                                       <p className="text-sm text-stone-500 mt-1">{m.descricao || 'Sem descrição'}</p>
                                    </CardContent>
                                 </Card>
                              ))
                           )}
                        </div>
                        <div className="space-y-4">
                           <h3 className="font-semibold text-stone-800 flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-500" /> Histórico Realizado</h3>
                            <div className="max-h-[400px] overflow-y-auto space-y-3 pr-2">
                              {isManLoading ? <Loader2 className="animate-spin" /> : manejos.map(m => (
                                 <div key={m.id} className="p-3 bg-white border rounded-lg shadow-sm">
                                    <div className="flex justify-between items-start">
                                       <div>
                                          <p className="font-medium text-stone-900 capitalize">{m.tipo}</p>
                                          <p className="text-xs text-stone-500">{formatDate(m.data)}</p>
                                       </div>
                                       {m.proxima_data && <Badge variant="secondary" className="text-[10px]">Prox: {formatDate(m.proxima_data)}</Badge>}
                                    </div>
                                 </div>
                              ))}
                           </div>
                           
                           <div className="p-4 bg-white border rounded-lg shadow-sm space-y-3">
                              <h4 className="text-sm font-semibold text-stone-700">Agendar Novo Manejo</h4>
                              <div className="grid grid-cols-2 gap-3">
                                 <Select value={novoManejo.tipo} onValueChange={(v) => setNovoManejo({ ...novoManejo, tipo: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                       <SelectItem value="vacina">Vacina</SelectItem>
                                       <SelectItem value="vermifugacao">Vermifugação</SelectItem>
                                       <SelectItem value="tratamento">Tratamento</SelectItem>
                                    </SelectContent>
                                 </Select>
                                 <Input type="date" value={novoManejo.proxima_data} onChange={(e) => setNovoManejo({ ...novoManejo, proxima_data: e.target.value })} />
                              </div>
                              <Button className="w-full" size="sm" onClick={async () => {
                                 if (!novoManejo.proxima_data) { setErrorMsg("Informe a próxima data"); return }
                                 const u = await agrogestao.auth.me()
                                 await agrogestao.entities.ManejoAnimal.create({
                                    propriedade_id: propriedadeId,
                                    animal_id: histDialog.id,
                                    tipo: novoManejo.tipo,
                                    descricao: 'Lembrete de manejo',
                                    data: new Date().toISOString().split('T')[0],
                                    proxima_data: novoManejo.proxima_data,
                                    observacoes: null,
                                    created_by: u?.email || null
                                 })
                                 setNovoManejo({ tipo: 'vacina', proxima_data: '' })
                                 queryClient.invalidateQueries({ queryKey: ["manejos_animais", histDialog.id] })
                              }}>Agendar</Button>
                           </div>
                        </div>
                     </div>
                  </TabsContent>

                  {histDialog?.categoria === 'bovino_leite' && (
                     <TabsContent value="producao" className="mt-0">
                        <Card>
                           <CardHeader>
                              <CardTitle>Produção de Leite (30 dias)</CardTitle>
                           </CardHeader>
                           <CardContent>
                              {(() => {
                                 const cutoff = new Date(); cutoff.setDate(cutoff.getDate()-30)
                                 const recent = (leiteResumo || []).filter(p => new Date(p.data) >= cutoff)
                                 const litros = recent.reduce((acc,p)=>acc+(typeof p.volume_litros==='number'?p.volume_litros:0),0)
                                 const media = recent.length ? litros / recent.length : 0
                                 return (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                       <div className="p-4 bg-emerald-50 rounded-lg text-center">
                                          <p className="text-sm text-emerald-600 uppercase font-semibold">Total Produzido</p>
                                          <p className="text-3xl font-bold text-emerald-800">{litros.toFixed(1)} L</p>
                                       </div>
                                       <div className="p-4 bg-blue-50 rounded-lg text-center">
                                          <p className="text-sm text-blue-600 uppercase font-semibold">Média Diária</p>
                                          <p className="text-3xl font-bold text-blue-800">{media.toFixed(1)} L</p>
                                       </div>
                                       <div className="p-4 bg-indigo-50 rounded-lg text-center">
                                          <p className="text-sm text-indigo-600 uppercase font-semibold">Registros</p>
                                          <p className="text-3xl font-bold text-indigo-800">{recent.length}</p>
                                       </div>
                                    </div>
                                 )
                              })()}
                           </CardContent>
                        </Card>
                     </TabsContent>
                  )}
               </div>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
